import type * as types from "./types"

const sendMessage = (msgRaw: string | types.MessageFromWebToNative): void => {
  if (!process.env.NATIVE_APP || !window.ReactNativeWebView) return

  let msg: string | undefined
  if (typeof msgRaw === `string`) msg = msgRaw
  else {
    try {
      msg = JSON.stringify(msgRaw)
    } catch {
      // some Objects might fail to stringify
    }
  }

  if (msg) window.ReactNativeWebView.postMessage(msg)
}

export const log = (...props) => sendMessage({ cmd: `log`, props })

export const openUrl = (
  ...props:
    | [string]
    | [
        string,
        {
          /**
           * @default 'done' label in the top left
           */
          dismissButtonStyle?: "close" | "cancel"
        },
      ]
) => sendMessage({ cmd: `browser`, props })

export const closeBrowser = () => sendMessage({ cmd: `closeBrowser` })

export const isNativeApp = () =>
  Boolean(process.env.NATIVE_APP && window.ReactNativeWebView)

/**
 * Time to wait for the native "coordsAck". No human is involved, so this only has to cover the
 * bridge round trip; missing it means this binary has no handler for the command.
 */
const ackTimeout = 3000

/**
 * Time to wait for the coordinates once acked. Generous, because it includes the OS permission
 * prompt that the user has to answer.
 */
const coordsTimeout = 120000

/**
 * Ask the native app for the device coordinates.
 *
 * Resolves with "denied" when the user rejects the OS prompt, and "undefined" when there was no
 * reply at all (ex. an OTA bundle running on an older binary that has no handler for this command).
 * Callers must keep those apart: retrying in the browser after a denial would re-prompt the user.
 */
export const requestCoords = (): Promise<
  types.Coords | "denied" | undefined
> => {
  if (!isNativeApp()) return Promise.resolve(undefined)

  return new Promise((resolve) => {
    const eventName: types.EventFromNative = `NATIVE_APP`
    let timeoutId: ReturnType<typeof setTimeout>

    const finish = (result: types.Coords | "denied" | undefined) => {
      clearTimeout(timeoutId)
      removeEventListener(eventName, onNativeEvent)
      resolve(result)
    }

    const onNativeEvent = (event: Event) => {
      const { cmd, props } = (event as CustomEvent<types.MessageFromNativeToWeb>)
        .detail

      /** Acked, so this binary owns the request: wait as long as the permission prompt needs. */
      if (cmd === `coordsAck`) {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => finish(undefined), coordsTimeout)
        return
      }
      if (cmd !== `coords`) return

      const result = (props as [types.CoordsResult] | undefined)?.[0]
      finish(!result ? undefined : `lat` in result ? result : `denied`)
    }

    timeoutId = setTimeout(() => finish(undefined), ackTimeout)

    addEventListener(eventName, onNativeEvent, { passive: true })
    sendMessage({ cmd: `getCoords` })
  })
}
